import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import useSectionReveal from '@/hooks/useSectionReveal';
import { solutions } from '@/mocks/sections';

gsap.registerPlugin(ScrollTrigger, useGSAP);

export default function SolutionsSection() {
  const ref = useRef<HTMLElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const prev = useRef(0);
  const [active, setActive] = useState(0);
  useSectionReveal(ref);

  // Preload all four images to avoid flashes
  useEffect(() => {
    solutions.forEach((s) => { const im = new Image(); im.src = s.image; });
  }, []);

  // Active-state tracking (desktop only)
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add('(min-width: 1024px)', () => {
      const items = gsap.utils.toArray<HTMLElement>('.sol-item', ref.current);
      items.forEach((el, i) => {
        ScrollTrigger.create({
          trigger: el,
          start: 'top 55%',
          end: 'bottom 55%',
          onToggle: (self) => { if (self.isActive) setActive(i); },
        });
      });
    });
    return () => mm.revert();
  }, { scope: ref });

  // Directional mask reveal between stacked images
  useEffect(() => {
    const from = prev.current;
    if (from === active || !stage.current) return;
    const frames = stage.current.querySelectorAll<HTMLElement>('.sol-frame');
    const next = frames[active];
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    frames.forEach((f, i) => {
      gsap.killTweensOf(f);
      gsap.set(f, { zIndex: i === from ? 1 : 0 });
    });
    gsap.set(next, { zIndex: 2 });
    if (reduce) {
      gsap.set(next, { clipPath: 'inset(0% 0% 0% 0%)' });
    } else {
      const forward = active > from;
      gsap.fromTo(next, { clipPath: forward ? 'inset(100% 0% 0% 0%)' : 'inset(0% 0% 100% 0%)' }, {
        clipPath: 'inset(0% 0% 0% 0%)', duration: 0.95, ease: 'power3.inOut',
      });
      const img = next.querySelector('img');
      if (img) gsap.fromTo(img, { y: forward ? 22 : -22 }, { y: 0, duration: 1.1, ease: 'power3.out' });
    }
    prev.current = active;
  }, [active]);

  return (
    <section ref={ref} id="windows" className="bg-background-100 px-4 md:px-10 py-20 md:py-32">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
          <div className="lg:col-span-7">
            <p data-reveal className="text-[11px] uppercase tracking-[0.24em] text-primary-600">Window solutions</p>
            <h2 data-reveal className="mt-4 font-heading font-light text-3xl md:text-6xl leading-[1.1] text-foreground-950">Four ways we bring the outside in.</h2>
          </div>
          <p data-reveal className="lg:col-span-4 lg:col-start-9 text-sm text-foreground-600 leading-relaxed">From a single bay window to a whole-house upgrade, every project is surveyed, supplied and installed by our own team.</p>
        </div>

        <div className="mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12">
          {/* Sticky image stage — desktop */}
          <div className="hidden lg:block lg:col-span-6">
            <div className="sticky top-[14vh]">
              <div ref={stage} className="relative w-full aspect-[4/3] overflow-hidden bg-background-200">
                {solutions.map((s, i) => (
                  <div key={s.n} className="sol-frame absolute inset-0 overflow-hidden" style={{ zIndex: i === 0 ? 2 : 0 }}>
                    <img loading="lazy" decoding="async" src={s.image} alt={s.title} title={`${s.title} services`} className="w-full h-[calc(100%+48px)] -mt-6 object-cover object-top will-change-transform" />
                  </div>
                ))}
              </div>
              <div className="mt-5 flex items-center justify-between text-[11px] uppercase tracking-[0.24em] text-foreground-600">
                <span className="relative h-4 overflow-hidden inline-block min-w-[2.5rem]">
                  {solutions.map((s, i) => (
                    <span key={s.n} className={`absolute left-0 top-0 transition-all duration-500 ease-out ${i === active ? 'translate-y-0 opacity-100' : i < active ? '-translate-y-full opacity-0' : 'translate-y-full opacity-0'}`}>{s.n} / 04</span>
                  ))}
                </span>
                <span className="text-foreground-500">{solutions[active].title}</span>
              </div>
            </div>
          </div>

          {/* Service list */}
          <div className="lg:col-span-6 min-w-0">
            {solutions.map((s, i) => {
              const on = i === active;
              return (
                <article key={s.n} className="sol-item relative lg:min-h-[72vh] flex flex-col justify-center py-10 lg:py-16">
                  <span className="absolute left-0 right-0 top-0 h-px bg-background-300/70" />
                  <span className={`absolute left-0 top-0 h-px w-full origin-left bg-primary-700 transition-transform duration-[900ms] ease-[cubic-bezier(0.65,0,0.35,1)] ${on ? 'lg:scale-x-100' : 'lg:scale-x-0'} scale-x-0`} />
                  <div data-reveal-img className="lg:hidden w-full h-[240px] md:h-[380px] overflow-hidden mb-6">
                    <img loading="lazy" decoding="async" src={s.image} alt={s.title} title={`${s.title} services`} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className={`transition-all duration-700 ease-out ${on ? 'lg:opacity-100 lg:translate-y-0' : 'lg:opacity-40 lg:translate-y-[10px]'}`}>
                    <p className="font-heading text-sm text-accent-700">{s.n}</p>
                    <h3 className="mt-3 font-heading font-light text-2xl md:text-4xl text-primary-900">{s.title}</h3>
                    <p className="mt-4 max-w-md text-sm md:text-base text-foreground-700 leading-relaxed">{s.text}</p>
                    <ul className="mt-6 max-w-md border-t border-background-300/70">
                      {s.points.map((p) => (
                        <li key={p} className="py-3 border-b border-background-300/70 text-sm text-foreground-800">{p}</li>
                      ))}
                    </ul>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}